import React, { useMemo } from "react";
import { useState } from "react";
import apiFetch from "../../utils/apiFetch";
import T4PreviewTopBar from "../../components/payroll/T4PreviewTopBar";

import { s } from "./styles";
import { getToken } from "./helpers";
import { useT4Preview } from "./useT4Preview";
import Slip from "./components/Slip";
import CutLine from "./components/CutLine";
import ReversePage from "./components/ReversePage";
import type { T4Employee } from "./types";

const TopBar = T4PreviewTopBar as any;

export default function T4EmployerSlips() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(currentYear);

  const { data, isLoading: loading, error: qError } = useT4Preview(year);

  const employer = data ? data.employer : null;
  const employees: T4Employee[] = data ? data.employees : [];
  const errMsg = qError instanceof Error ? qError.message : null;
  const error = errMsg ? "Could not load T4 data: " + errMsg : "";

  const pages = useMemo(() => {
    const out: T4Employee[][] = [];
    for (let i = 0; i < employees.length; i += 2) {
      out.push(employees.slice(i, i + 2));
    }
    return out;
  }, [employees]);

  const handleDownload = async () => {
    const token = getToken();
    const res = await apiFetch(
      "/api/v1/payroll/taxes/t4-employer-slips-v2.pdf?year=" + year,
      { headers: { Authorization: "Bearer " + token } }
    );
    if (!res.ok) {
      alert("Could not generate PDF");
      return;
    }
    const blob = await res.blob();
    window.open(URL.createObjectURL(blob), "_blank");
  };

  return (
    <div
      style={{
        background: "#EDEFF2",
        fontFamily: "Arial, Helvetica, sans-serif",
        color: "#000",
        width: "100%",
        minHeight: "100vh",
      }}
    >
      <style>{`
        @media print {
          @page { size: letter; margin: 0; }
          .t4-noprint { display: none !important; }
          .t4-page { box-shadow: none !important; margin: 0 !important; width: auto !important; max-width: none !important; min-height: 0 !important; height: auto !important; padding: 0.35in !important; page-break-after: always !important; break-after: page !important; }
          .t4-page:last-child { page-break-after: auto !important; break-after: auto !important; }
          .t4-page * { max-height: none !important; }
        }
        .t4-cutline { border-top: 1px dashed #666; margin: 22px 0; position: relative; }
        .t4-cutline:after { content: "\\2702"; position: absolute; left: 6px; top: -10px; font-size: 14px; background: #fff; padding: 0 5px; color: #555; }
      `}</style>

      <TopBar
        title="T4 employer slips"
        subtitle={
          employees.length === 0
            ? "No employee data for " + year
            : "Employer copy · file with XML · two slips per page · " +
              employees.length +
              " employee" +
              (employees.length === 1 ? "" : "s")
        }
        year={year}
        onYearChange={setYear}
        loading={loading}
        error={!!error}
        downloadDisabled={loading || employees.length === 0}
        onDownload={handleDownload}
      />

      {loading && (
        <div
          style={{ ...s.page, textAlign: "center", padding: 60, fontSize: 14 }}
        >
          Loading T4 data for {year}...
        </div>
      )}

      {error && !loading && (
        <div style={{ ...s.page, padding: 40 }}>
          <div
            style={{
              background: "#FEF2F2",
              border: "1px solid #FCA5A5",
              borderRadius: 8,
              padding: "12px 16px",
              color: "#991B1B",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {error}
          </div>
        </div>
      )}

      {!loading && !error && employees.length === 0 && (
        <div
          style={{ ...s.page, textAlign: "center", padding: 60, fontSize: 14 }}
        >
          No paycheques have been issued for {year} yet. Complete a payroll run
          before previewing T4 slips.
        </div>
      )}

      {!loading && !error && employer &&
        pages.map((pair, pi) => (
          <div key={pi} className="t4-page" style={s.page}>
            {pair.map((emp, idx) => (
              <React.Fragment key={String(emp.id) || idx}>
                <Slip employer={employer} employee={emp} year={year} />
                <CutLine />
              </React.Fragment>
            ))}
          </div>
        ))}

      {!loading && !error && employees.length > 0 && (
        <div className="t4-page" style={s.page}>
          <ReversePage />
        </div>
      )}
    </div>
  );
}